import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { orderAPI, settingsAPI, staffAPI } from '../utils/api';
import { formatPrice } from '../utils/translations';
import { generateReceiptHTML, generateKitchenTicketHTML, printWithDialog } from '../utils/webPrint';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LANG = {
  vi: {
    logout: 'Thoát',
    orders: 'Đơn hàng',
    newOrder: 'Tạo đơn',
    bills: 'Hóa đơn',
    active: 'Đang xử lý',
    served: 'Đã phục vụ',
    all: 'Tất cả',
    noOrders: 'Không có đơn hàng',
    table: 'Bàn',
    approve: 'Duyệt đơn',
    startCooking: 'Bắt đầu nấu',
    markReady: 'Đã xong',
    markServed: 'Đã phục vụ',
    selectTable: 'Chọn bàn',
    searchMenu: 'Tìm món...',
    items: 'món',
    placeOrder: 'Gửi đơn',
    sending: 'Đang gửi...',
    orderPlaced: 'Đã đặt đơn',
    activeSessions: 'Phiên bàn đang mở',
    noSessions: 'Không có phiên nào',
    orderCount: 'đơn',
    opened: 'Mở lúc',
    printInvoice: 'In hóa đơn',
    printKitchenToken: 'In phieu bep',
    newOrderAlert: 'Don hang moi!',
    soundOn: 'Am thanh',
    soundOff: 'Tat tieng',
    notificationsEnabled: 'Thong bao da bat',
    enableNotifications: 'Bat thong bao',
    newOrderFrom: 'Don moi tu Ban',
    closeTable: 'Dong ban',
    closingTable: 'Dang dong...',
    tableClosed: 'Da dong ban!',
    paymentMethod: 'Phuong thuc',
    cash: 'Tien mat',
    card: 'The',
    discount: 'Giam gia',
    subtotal: 'Tam tinh',
    total: 'Tong',
    cardFee: 'Phi the',
    confirmClose: 'Xac nhan & In hoa don',
    cancelClose: 'Huy',
    showQR: 'QR Thanh toan',
    scanToPay: 'Quet de thanh toan',
    bankTransfer: 'Chuyen khoan',
    closeQR: 'Dong',
    qrNotConfigured: 'Chua cai dat ngan hang',
  },
  en: {
    logout: 'Logout',
    orders: 'Orders',
    newOrder: 'New Order',
    bills: 'Bills',
    active: 'Active',
    served: 'Served',
    all: 'All',
    noOrders: 'No orders found',
    table: 'Table',
    approve: 'Approve',
    startCooking: 'Start Cooking',
    markReady: 'Mark Ready',
    markServed: 'Mark Served',
    selectTable: 'Select Table',
    searchMenu: 'Search menu...',
    items: 'items',
    placeOrder: 'Place Order',
    sending: 'Sending...',
    orderPlaced: 'Order placed',
    activeSessions: 'Active Table Sessions',
    noSessions: 'No active sessions',
    orderCount: 'orders',
    opened: 'Opened',
    printInvoice: 'Print Invoice',
    printKitchenToken: 'Print Kitchen Token',
    newOrderAlert: 'New Order!',
    soundOn: 'Sound On',
    soundOff: 'Sound Off',
    notificationsEnabled: 'Notifications enabled',
    enableNotifications: 'Enable Notifications',
    newOrderFrom: 'New order from Table',
    closeTable: 'Close Table',
    closingTable: 'Closing...',
    tableClosed: 'Table Closed!',
    paymentMethod: 'Payment',
    cash: 'Cash',
    card: 'Card',
    discount: 'Discount',
    subtotal: 'Subtotal',
    total: 'Total',
    cardFee: 'Card Fee',
    confirmClose: 'Confirm & Print Bill',
    cancelClose: 'Cancel',
    showQR: 'QR Payment',
    scanToPay: 'Scan to pay',
    bankTransfer: 'Bank Transfer',
    closeQR: 'Close',
    qrNotConfigured: 'Bank not configured',
  },
};

const StaffDashboard = () => {
  const navigate = useNavigate();
  const [staff, setStaff] = useState(null);
  const [view, setView] = useState('orders'); // orders | bill
  const [orders, setOrders] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('active');

  const [billSession, setBillSession] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [lang, setLang] = useState('vi');

  // Close table state
  const [closeSession, setCloseSession] = useState(null);
  const [closePayMethod, setClosePayMethod] = useState('cash');
  const [closeDiscount, setCloseDiscount] = useState(0);
  const [closing, setClosing] = useState(false);
  const [closeSuccess, setCloseSuccess] = useState(null);

  // QR payment state
  const [showPaymentQR, setShowPaymentQR] = useState(null); // { qr_url, amount, bank_name, account_number, account_holder }

  // Notification state
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [notificationPermission, setNotificationPermission] = useState('default');
  const [newOrderAlert, setNewOrderAlert] = useState(null);
  const knownOrderIdsRef = useRef(new Set());
  const isFirstLoadRef = useRef(true);

  const token = localStorage.getItem('staff_token');
  const t = LANG[lang];

  const checkAuth = useCallback(async () => {
    if (!token) { navigate('/staff'); return; }
    try {
      const res = await staffAPI.me(token);
      setStaff(res.data);
    } catch {
      localStorage.removeItem('staff_token');
      localStorage.removeItem('staff_user');
      navigate('/staff');
    }
  }, [token, navigate]);

  // Request browser notification permission
  useEffect(() => {
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission);
    }
  }, []);

  const requestNotificationPermission = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
    }
  };

  // Play notification sound
  const playNotificationSound = useCallback((soundUrl) => {
    if (!soundEnabled) return;
    try {
      if (soundUrl) {
        const audio = new Audio(soundUrl);
        audio.volume = 0.7;
        audio.play().catch(() => {});
      } else {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = 880;
        gain.gain.value = 0.4;
        osc.start();
        osc.stop(ctx.currentTime + 0.15);
        setTimeout(() => {
          const osc2 = ctx.createOscillator();
          const gain2 = ctx.createGain();
          osc2.connect(gain2);
          gain2.connect(ctx.destination);
          osc2.frequency.value = 1100;
          gain2.gain.value = 0.4;
          osc2.start();
          osc2.stop(ctx.currentTime + 0.15);
        }, 180);
      }
    } catch (e) {}
  }, [soundEnabled]);

  // Send browser notification
  const sendBrowserNotification = useCallback((order) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      const tableName = order.table || '?';
      const itemCount = (order.items || []).reduce((s, i) => s + (i.quantity || 1), 0);
      const notification = new Notification(lang === 'vi' ? 'Don hang moi!' : 'New Order!', {
        body: `${lang === 'vi' ? 'Ban' : 'Table'} ${tableName} - ${itemCount} ${lang === 'vi' ? 'mon' : 'items'} - ${formatPrice(order.total || 0)}`,
        icon: '/staff-icon-192.png',
        badge: '/staff-icon-192.png',
        tag: `order-${order.id}`,
        requireInteraction: true,
      });
      notification.onclick = () => {
        window.focus();
        setView('orders');
        setStatusFilter('active');
        notification.close();
      };
    }
  }, [lang]);

  // Check for new orders and trigger notifications
  const checkForNewOrders = useCallback((allOrders, soundUrl) => {
    const pendingOrders = allOrders.filter(o => o.status === 'pending_approval');

    if (isFirstLoadRef.current) {
      pendingOrders.forEach(o => knownOrderIdsRef.current.add(o.id));
      isFirstLoadRef.current = false;
      return;
    }

    const newOrders = pendingOrders.filter(o => !knownOrderIdsRef.current.has(o.id));

    if (newOrders.length > 0) {
      playNotificationSound(soundUrl);

      const firstNew = newOrders[0];
      setNewOrderAlert({
        count: newOrders.length,
        table: firstNew.table,
        orderNumber: firstNew.order_number,
      });
      setTimeout(() => setNewOrderAlert(null), 6000);

      newOrders.forEach(order => sendBrowserNotification(order));
      newOrders.forEach(o => knownOrderIdsRef.current.add(o.id));
    }

    // Cleanup IDs no longer in the system
    knownOrderIdsRef.current.forEach(id => {
      if (!allOrders.find(o => o.id === id)) {
        knownOrderIdsRef.current.delete(id);
      }
    });
  }, [playNotificationSound, sendBrowserNotification]);

  const loadData = useCallback(async () => {
    try {
      const [ordersRes, settingsRes] = await Promise.all([
        orderAPI.getAll(),
        settingsAPI.get(),
      ]);
      setOrders(ordersRes.data);
      setSettings(settingsRes.data);

      // Check for new orders and trigger notifications
      checkForNewOrders(ordersRes.data, settingsRes.data?.notification_sound_url);
    } catch (e) {
      console.error('Load error:', e);
    }
    setLoading(false);
  }, [checkForNewOrders]);

  const loadSessions = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/table-sessions`);
      const data = await res.json();
      setSessions(data);
    } catch (e) {
      console.error('Sessions error:', e);
    }
  }, []);
  useEffect(() => { checkAuth(); }, [checkAuth]);
  useEffect(() => {
    // Swap manifest for Staff PWA
    const manifestLink = document.querySelector('link[rel="manifest"]');
    if (manifestLink) manifestLink.setAttribute('href', '/staff-manifest.json');
    if (staff) { loadData(); loadSessions(); }
  }, [staff, loadData, loadSessions]);

  // Auto-refresh orders every 8 seconds
  useEffect(() => {
    if (!staff) return;
    const interval = setInterval(() => { loadData(); loadSessions(); }, 8000);
    return () => clearInterval(interval);
  }, [staff, loadData, loadSessions]);

  const logout = () => {
    localStorage.removeItem('staff_token');
    localStorage.removeItem('staff_user');
    navigate('/staff');
  };

  const filteredOrders = orders.filter(o => {
    if (statusFilter === 'active') return ['pending_approval', 'pending', 'preparing', 'ready'].includes(o.status);
    if (statusFilter === 'served') return o.status === 'served';
    return true;
  }).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const updateStatus = async (id, status, currentStatus) => {
    try {
      if (currentStatus === 'pending_approval') {
        await orderAPI.transfer(id);
      } else {
        await orderAPI.updateStatus(id, status);
      }
      await loadData();
    } catch (e) {
      alert('Failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const printBill = (session) => {
    if (!session || !settings) return;
    const allItems = (session.orders || []).flatMap(o => o.items || []);
    const subtotal = allItems.reduce((s, i) => s + (i.price || 0) * (i.quantity || 1), 0);
    const billData = {
      order_number: `T${session.table}`,
      table: session.table,
      items: allItems,
      subtotal,
      discount_percent: 0,
      discount: 0,
      card_fee_percent: 0,
      card_fee: 0,
      total: subtotal,
      payment_method: 'cash',
      created_at: session.created_at,
    };
    const html = generateReceiptHTML(billData, settings);
    printWithDialog(html);
  };

  const printKitchenToken = (order) => {
    if (!order || !settings) return;
    const html = generateKitchenTicketHTML(order, settings);
    printWithDialog(html);
  };

  // Close table: finalize bill and close session
  const closeTable = async () => {
    if (!closeSession || closing) return;
    setClosing(true);
    try {
      await fetch(`${API_URL}/api/bill/table/finalize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: closeSession.id,
          discount_percent: closeDiscount,
          payment_method: closePayMethod,
        }),
      });

      // Print the final bill
      const allItems = (closeSession.orders || []).flatMap(o => o.items || []);
      const subtotal = allItems.reduce((s, i) => s + (i.price || 0) * (i.quantity || 1), 0);
      const discountAmt = subtotal * (closeDiscount / 100);
      const afterDiscount = subtotal - discountAmt;
      const cardFeePercent = settings?.credit_card_fee_percent || 5;
      const feeAmt = closePayMethod === 'card' && settings?.enable_credit_card_fee
        ? afterDiscount * cardFeePercent / 100 : 0;
      const finalTotal = afterDiscount + feeAmt;

      const billData = {
        order_number: `T${closeSession.table}`,
        table: closeSession.table,
        items: allItems,
        subtotal,
        discount_percent: closeDiscount,
        discount: discountAmt,
        card_fee_percent: closePayMethod === 'card' ? cardFeePercent : 0,
        card_fee: feeAmt,
        total: finalTotal,
        payment_method: closePayMethod,
        created_at: closeSession.created_at,
      };
      const html = generateReceiptHTML(billData, settings);
      printWithDialog(html);

      setCloseSuccess(closeSession.table);
      setCloseSession(null);
      setCloseDiscount(0);
      setClosePayMethod('cash');
      await loadData();
      await loadSessions();
      setTimeout(() => setCloseSuccess(null), 3000);
    } catch (e) {
      alert('Failed to close table: ' + (e.message || 'Unknown error'));
    }
    setClosing(false);
  };

  // Show QR payment for a session
  const showQRForSession = async (session) => {
    const allItems = (session.orders || []).flatMap(o => o.items || []);
    const sub = allItems.reduce((s, i) => s + (i.price || 0) * (i.quantity || 1), 0);
    const desc = `Benz Eatery Ban ${session.table}`;
    try {
      const res = await fetch(`${API_URL}/api/payment/vietqr?amount=${sub}&description=${encodeURIComponent(desc)}`);
      if (!res.ok) {
        const err = await res.json();
        alert(err.detail || t.qrNotConfigured);
        return;
      }
      const data = await res.json();
      setShowPaymentQR(data);
    } catch (e) {
      alert(t.qrNotConfigured);
    }
  };

  const statusColor = (s) => ({
    pending_approval: 'bg-purple-500/20 text-purple-300 border-purple-500/30',
    pending: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
    preparing: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
    ready: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
    served: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
    cancelled: 'bg-red-500/20 text-red-400 border-red-500/30',
  }[s] || 'bg-slate-700 text-slate-300');

  const nextStatus = (s) => ({
    pending_approval: 'pending',
    pending: 'preparing',
    preparing: 'ready',
    ready: 'served',
  }[s]);

  const nextStatusLabel = (s) => ({
    pending_approval: t.approve,
    pending: t.startCooking,
    preparing: t.markReady,
    ready: t.markServed,
  }[s]);

  if (loading || !staff) {
    return (
      <div className="sd-loading">
        <div className="sd-spinner" />
        <style>{STYLES}</style>
      </div>
    );
  }

  return (
    <div className="sd-root" data-testid="staff-dashboard">
      {/* Top bar */}
      <header className="sd-header">
        <div className="sd-header-left">
          <span className="sd-avatar">{staff.display_name?.charAt(0) || 'S'}</span>
          <span className="sd-greeting">{staff.display_name}</span>
        </div>
        <div className="sd-header-right">
          <button
            onClick={() => setSoundEnabled(!soundEnabled)}
            className={`sd-sound-btn ${soundEnabled ? 'active' : ''}`}
            data-testid="staff-sound-toggle"
            title={soundEnabled ? t.soundOn : t.soundOff}
          >
            {soundEnabled ? '♪' : '♪'}
          </button>
          {notificationPermission !== 'granted' && (
            <button
              onClick={requestNotificationPermission}
              className="sd-notif-btn"
              data-testid="staff-enable-notifications"
              title={t.enableNotifications}
            >
              ⚡
            </button>
          )}
          <button onClick={logout} className="sd-logout" data-testid="staff-logout-btn">{t.logout}</button>
        </div>
      </header>

      {/* New order notification banner */}
      {newOrderAlert && (
        <div className="sd-new-order-banner" data-testid="staff-new-order-alert" onClick={() => { setView('orders'); setStatusFilter('active'); setNewOrderAlert(null); }}>
          <span className="sd-alert-icon">!</span>
          <span className="sd-alert-text">
            {t.newOrderFrom} {newOrderAlert.table} — #{newOrderAlert.orderNumber}
            {newOrderAlert.count > 1 && ` (+${newOrderAlert.count - 1})`}
          </span>
        </div>
      )}

      {/* Lang toggle + Tab bar */}
      <div className="sd-lang-row">
        <button onClick={() => setLang('vi')} className={`sd-lang-btn ${lang === 'vi' ? 'active' : ''}`}>VI</button>
        <button onClick={() => setLang('en')} className={`sd-lang-btn ${lang === 'en' ? 'active' : ''}`}>EN</button>
      </div>
      <nav className="sd-tabs">
        {[
          { id: 'orders', label: t.orders, icon: 'list' },
          { id: 'bill', label: t.bills, icon: 'bill' },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setView(tab.id)}
            className={`sd-tab ${view === tab.id ? 'active' : ''}`}
            data-testid={`staff-tab-${tab.id}`}
          >
            <span className="sd-tab-icon">{tab.icon}</span>
            <span>{tab.label}</span>
            {tab.id === 'orders' && filteredOrders.filter(o => ['pending_approval','pending'].includes(o.status)).length > 0 && (
              <span className="sd-badge">{filteredOrders.filter(o => ['pending_approval','pending'].includes(o.status)).length}</span>
            )}
          </button>
        ))}
      </nav>

      {/* Content */}
      <main className="sd-content">
        {/* ============ ORDERS VIEW ============ */}
        {view === 'orders' && (
          <div data-testid="staff-orders-view">
            <div className="sd-filter-row">
              {['active', 'served', 'all'].map(f => (
                <button key={f} onClick={() => setStatusFilter(f)}
                  className={`sd-filter-btn ${statusFilter === f ? 'active' : ''}`}
                  data-testid={`staff-filter-${f}`}
                >{f === 'active' ? t.active : f === 'served' ? t.served : t.all}</button>
              ))}
              <button onClick={() => loadData()} className="sd-filter-btn sd-refresh" data-testid="staff-refresh-btn">↻</button>
            </div>

            {filteredOrders.length === 0 && (
              <div className="sd-empty">{t.noOrders}</div>
            )}

            <div className="sd-order-list">
              {filteredOrders.map(order => (
                <div key={order.id} className="sd-order-card" data-testid={`staff-order-${order.id}`}>
                  <div className="sd-order-top">
                    <div>
                      <span className="sd-order-num">#{order.order_number}</span>
                      <span className="sd-order-table">{t.table} {order.table}</span>
                    </div>
                    <span className={`sd-status ${statusColor(order.status)}`}>{order.status.replace('_', ' ')}</span>
                  </div>
                  <div className="sd-order-items">
                    {(order.items || []).map((item, i) => (
                      <div key={i} className="sd-order-item">
                        <span>{typeof item.name === 'string' ? item.name : (item.name?.[lang] || item.name?.vi || item.name?.en || 'Item')} x{item.quantity}</span>
                        <span className="sd-item-price">{formatPrice(item.price * item.quantity)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="sd-order-footer">
                    <span className="sd-order-total">{formatPrice(order.total || 0)}</span>
                    <span className="sd-order-time">{new Date(order.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                  {nextStatus(order.status) && (
                    <button
                      onClick={() => updateStatus(order.id, nextStatus(order.status), order.status)}
                      className="sd-action-btn"
                      data-testid={`staff-action-${order.id}`}
                    >
                      {nextStatusLabel(order.status)}
                    </button>
                  )}
                  <button
                    onClick={() => printKitchenToken(order)}
                    className="sd-print-token-btn"
                    data-testid={`staff-print-token-${order.id}`}
                  >
                    {t.printKitchenToken}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ============ BILLS VIEW ============ */}
        {view === 'bill' && (
          <div data-testid="staff-bills-view">
            {closeSuccess && (
              <div className="sd-success" data-testid="staff-close-success">{t.tableClosed} — {t.table} {closeSuccess}</div>
            )}
            <div className="sd-section-label">{t.activeSessions}</div>
            {sessions.length === 0 && <div className="sd-empty">{t.noSessions}</div>}
            <div className="sd-session-list">
              {sessions.map(session => (
                <div key={session.id} className="sd-session-card" data-testid={`staff-session-${session.id}`}>
                  <div className="sd-session-top">
                    <span className="sd-session-table">{t.table} {session.table}</span>
                    <span className="sd-session-count">{session.order_count || 0} {t.orderCount}</span>
                  </div>
                  <div className="sd-session-total">
                    {formatPrice(session.total_amount || 0)}
                  </div>
                  <div className="sd-session-time">
                    {t.opened}: {new Date(session.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                  <div className="sd-session-actions">
                    <button onClick={() => showQRForSession(session)} className="sd-qr-btn" data-testid={`staff-qr-${session.id}`}>
                      {t.showQR}
                    </button>
                    <button onClick={() => printBill(session)} className="sd-print-btn" data-testid={`staff-print-${session.id}`}>
                      {t.printInvoice}
                    </button>
                    <button
                      onClick={() => { setCloseSession(session); setCloseDiscount(0); setClosePayMethod('cash'); }}
                      className="sd-close-table-btn"
                      data-testid={`staff-close-${session.id}`}
                    >
                      {t.closeTable}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Close Table Modal */}
        {closeSession && (
          <div className="sd-modal-overlay" data-testid="close-table-modal">
            <div className="sd-modal">
              <div className="sd-modal-header">
                <span className="sd-modal-title">{t.closeTable} — {t.table} {closeSession.table}</span>
              </div>

              {/* Items summary */}
              <div className="sd-modal-items">
                {(closeSession.orders || []).flatMap(o => o.items || []).map((item, i) => (
                  <div key={i} className="sd-modal-item">
                    <span>{typeof item.name === 'string' ? item.name : (item.name?.[lang] || item.name?.vi || item.name?.en || 'Item')} x{item.quantity}</span>
                    <span>{formatPrice((item.price || 0) * (item.quantity || 1))}</span>
                  </div>
                ))}
              </div>

              {/* Discount */}
              <div className="sd-modal-section">
                <span className="sd-modal-label">{t.discount}</span>
                <div className="sd-discount-row">
                  {[0, 5, 10, 15, 20].map(d => (
                    <button
                      key={d}
                      onClick={() => setCloseDiscount(d)}
                      className={`sd-discount-btn ${closeDiscount === d ? 'active' : ''}`}
                      data-testid={`close-discount-${d}`}
                    >
                      {d}%
                    </button>
                  ))}
                </div>
              </div>

              {/* Payment Method */}
              <div className="sd-modal-section">
                <span className="sd-modal-label">{t.paymentMethod}</span>
                <div className="sd-pay-row">
                  <button
                    onClick={() => setClosePayMethod('cash')}
                    className={`sd-pay-btn ${closePayMethod === 'cash' ? 'active cash' : ''}`}
                    data-testid="close-pay-cash"
                  >
                    {t.cash}
                  </button>
                  <button
                    onClick={() => setClosePayMethod('card')}
                    className={`sd-pay-btn ${closePayMethod === 'card' ? 'active card' : ''}`}
                    data-testid="close-pay-card"
                  >
                    {t.card}
                  </button>
                </div>
              </div>

              {/* Bill Summary */}
              {(() => {
                const allItems = (closeSession.orders || []).flatMap(o => o.items || []);
                const sub = allItems.reduce((s, i) => s + (i.price || 0) * (i.quantity || 1), 0);
                const disc = sub * (closeDiscount / 100);
                const afterDisc = sub - disc;
                const feePct = settings?.credit_card_fee_percent || 5;
                const fee = closePayMethod === 'card' && settings?.enable_credit_card_fee ? afterDisc * feePct / 100 : 0;
                const fin = afterDisc + fee;
                return (
                  <div className="sd-modal-summary">
                    <div className="sd-summary-row"><span>{t.subtotal}</span><span>{formatPrice(sub)}</span></div>
                    {closeDiscount > 0 && <div className="sd-summary-row discount"><span>{t.discount} ({closeDiscount}%)</span><span>-{formatPrice(disc)}</span></div>}
                    {fee > 0 && <div className="sd-summary-row fee"><span>{t.cardFee} ({feePct}%)</span><span>+{formatPrice(fee)}</span></div>}
                    <div className="sd-summary-total"><span>{t.total}</span><span>{formatPrice(fin)}</span></div>
                  </div>
                );
              })()}

              {/* Actions */}
              <div className="sd-modal-actions">
                <button
                  onClick={() => setCloseSession(null)}
                  className="sd-modal-cancel"
                  data-testid="close-table-cancel"
                >
                  {t.cancelClose}
                </button>
                <button
                  onClick={closeTable}
                  disabled={closing}
                  className="sd-modal-confirm"
                  data-testid="close-table-confirm"
                >
                  {closing ? t.closingTable : t.confirmClose}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* QR Payment Modal */}
      {showPaymentQR && (
        <div className="sd-qr-overlay" data-testid="payment-qr-modal" onClick={() => setShowPaymentQR(null)}>
          <div className="sd-qr-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sd-qr-header">{t.scanToPay}</div>
            <div className="sd-qr-amount">{formatPrice(showPaymentQR.amount)}</div>
            <img
              src={showPaymentQR.qr_url}
              alt="VietQR Payment"
              className="sd-qr-image"
            />
            <div className="sd-qr-bank">{showPaymentQR.bank_name}</div>
            <div className="sd-qr-account">{showPaymentQR.account_number}</div>
            <div className="sd-qr-holder">{showPaymentQR.account_holder}</div>
            <button onClick={() => setShowPaymentQR(null)} className="sd-qr-close" data-testid="close-payment-qr">
              {t.closeQR}
            </button>
          </div>
        </div>
      )}

      <style>{STYLES}</style>
    </div>
  );
};

const STYLES = `
  .sd-loading {
    min-height:100vh; display:flex; align-items:center; justify-content:center; background:#0f172a;
  }
  .sd-spinner {
    width:28px; height:28px; border:3px solid #f59e0b; border-top-color:transparent;
    border-radius:50%; animation:sd-spin .6s linear infinite;
  }
  @keyframes sd-spin { to { transform:rotate(360deg); } }

  .sd-root {
    min-height:100vh; background:#0f172a; color:#e2e8f0;
    font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;
    display:flex; flex-direction:column; max-width:480px; margin:0 auto;
    -webkit-font-smoothing:antialiased;
  }

  .sd-header {
    display:flex; align-items:center; justify-content:space-between;
    padding:14px 16px; background:#1e293b; border-bottom:1px solid #334155;
    position:sticky; top:0; z-index:30;
  }
  .sd-header-left { display:flex; align-items:center; gap:10px; }
  .sd-header-right { display:flex; align-items:center; gap:8px; }
  .sd-avatar {
    width:34px; height:34px; background:linear-gradient(135deg,#f59e0b,#ea580c);
    border-radius:10px; display:flex; align-items:center; justify-content:center;
    font-weight:800; font-size:16px; color:white;
  }
  .sd-greeting { font-weight:600; font-size:15px; }
  .sd-logout {
    background:#334155; color:#94a3b8; border:none; padding:8px 14px;
    border-radius:10px; font-size:12px; font-weight:600; cursor:pointer;
  }

  .sd-sound-btn {
    width:34px; height:34px; border-radius:10px; border:1px solid #334155;
    background:#1e293b; color:#64748b; font-size:16px; cursor:pointer;
    display:flex; align-items:center; justify-content:center; transition:all .15s;
  }
  .sd-sound-btn.active { background:#065f46; color:#6ee7b7; border-color:#065f46; }
  .sd-notif-btn {
    width:34px; height:34px; border-radius:10px; border:1px solid #f59e0b;
    background:#f59e0b20; color:#f59e0b; font-size:14px; cursor:pointer;
    display:flex; align-items:center; justify-content:center; animation:sd-pulse 2s infinite;
  }
  @keyframes sd-pulse {
    0%,100% { opacity:1; }
    50% { opacity:0.5; }
  }

  .sd-new-order-banner {
    display:flex; align-items:center; gap:10px; padding:12px 16px;
    background:linear-gradient(135deg,#f59e0b,#ea580c); color:white;
    font-weight:700; font-size:14px; cursor:pointer; animation:sd-slide-down .3s ease-out;
    position:sticky; top:63px; z-index:29;
  }
  .sd-alert-icon {
    width:24px; height:24px; background:white; color:#ea580c;
    border-radius:50%; display:flex; align-items:center; justify-content:center;
    font-weight:900; font-size:14px; flex-shrink:0;
  }
  .sd-alert-text { flex:1; }
  @keyframes sd-slide-down {
    from { transform:translateY(-100%); opacity:0; }
    to { transform:translateY(0); opacity:1; }
  }

  .sd-tabs {
    display:flex; background:#1e293b; border-bottom:1px solid #334155;
    position:sticky; top:100px; z-index:28;
  }
  .sd-lang-row {
    display:flex; gap:4px; justify-content:flex-end; padding:8px 16px 0;
    background:#1e293b; position:sticky; top:63px; z-index:28;
  }
  .sd-lang-btn {
    padding:4px 10px; border-radius:8px; border:1px solid #334155;
    background:transparent; color:#64748b; font-size:11px; font-weight:700;
    cursor:pointer;
  }
  .sd-lang-btn.active { background:#f59e0b; color:#0f172a; border-color:#f59e0b; }
  .sd-tab {
    flex:1; display:flex; flex-direction:column; align-items:center; gap:2px;
    padding:10px 4px; border:none; background:none; color:#64748b;
    font-size:11px; font-weight:600; cursor:pointer; position:relative;
    transition:color .15s;
  }
  .sd-tab.active { color:#f59e0b; }
  .sd-tab.active::after {
    content:''; position:absolute; bottom:0; left:20%; right:20%;
    height:2px; background:#f59e0b; border-radius:1px;
  }
  .sd-tab-icon { font-size:18px; }
  .sd-badge {
    position:absolute; top:4px; right:calc(50% - 20px);
    background:#ef4444; color:white; font-size:10px; font-weight:700;
    min-width:18px; height:18px; border-radius:9px; display:flex;
    align-items:center; justify-content:center; padding:0 4px;
  }

  .sd-content { flex:1; padding:16px; padding-bottom:100px; }

  /* Filters */
  .sd-filter-row { display:flex; gap:8px; margin-bottom:14px; }
  .sd-filter-btn {
    padding:8px 14px; border-radius:10px; border:1px solid #334155;
    background:#1e293b; color:#94a3b8; font-size:12px; font-weight:600;
    cursor:pointer; text-transform:capitalize;
  }
  .sd-filter-btn.active { background:#f59e0b; color:#0f172a; border-color:#f59e0b; }
  .sd-refresh { margin-left:auto; }

  .sd-empty { text-align:center; color:#475569; padding:40px 0; font-size:14px; }

  /* Order cards */
  .sd-order-list { display:flex; flex-direction:column; gap:10px; }
  .sd-order-card {
    background:#1e293b; border:1px solid #334155; border-radius:16px;
    padding:14px; transition:border-color .15s;
  }
  .sd-order-top { display:flex; justify-content:space-between; align-items:center; margin-bottom:10px; }
  .sd-order-num { font-weight:800; font-size:16px; margin-right:8px; }
  .sd-order-table { color:#94a3b8; font-size:12px; font-weight:600; }
  .sd-status {
    padding:4px 10px; border-radius:8px; font-size:11px; font-weight:700;
    text-transform:uppercase; letter-spacing:0.3px; border:1px solid;
  }
  .sd-order-items { border-top:1px solid #1e293b; padding-top:8px; }
  .sd-order-item {
    display:flex; justify-content:space-between; padding:3px 0;
    font-size:13px; color:#cbd5e1;
  }
  .sd-item-price { color:#94a3b8; font-size:12px; }
  .sd-order-footer {
    display:flex; justify-content:space-between; align-items:center;
    margin-top:10px; padding-top:8px; border-top:1px solid #334155;
  }
  .sd-order-total { font-weight:700; color:#f59e0b; }
  .sd-order-time { font-size:11px; color:#64748b; }
  .sd-action-btn {
    width:100%; margin-top:10px; padding:12px; border:none; border-radius:12px;
    background:linear-gradient(135deg,#f59e0b,#ea580c); color:white;
    font-size:14px; font-weight:700; cursor:pointer; transition:opacity .15s;
  }
  .sd-action-btn:active { opacity:0.8; }
  .sd-print-token-btn {
    width:100%; margin-top:6px; padding:10px; border:1px solid #334155; border-radius:10px;
    background:#1e293b; color:#94a3b8;
    font-size:12px; font-weight:600; cursor:pointer; transition:all .15s; font-family:inherit;
  }
  .sd-print-token-btn:hover { background:#334155; color:#e2e8f0; }
  .sd-print-token-btn:active { background:#475569; }

  /* Bills */
  .sd-section-label {
    font-size:12px; font-weight:700; color:#64748b; text-transform:uppercase;
    letter-spacing:0.5px; margin-bottom:10px;
  }
  .sd-success {
    background:#065f46; color:#6ee7b7; padding:12px; border-radius:12px;
    font-size:14px; font-weight:600; text-align:center; margin-bottom:14px;
  }
  .sd-session-list { display:flex; flex-direction:column; gap:10px; }
  .sd-session-card {
    background:#1e293b; border:1px solid #334155; border-radius:16px; padding:14px;
  }
  .sd-session-top { display:flex; justify-content:space-between; align-items:center; margin-bottom:6px; }
  .sd-session-table { font-weight:800; font-size:18px; }
  .sd-session-count { color:#94a3b8; font-size:12px; font-weight:600; }
  .sd-session-total { font-size:22px; font-weight:800; color:#f59e0b; }
  .sd-session-time { font-size:11px; color:#64748b; margin-top:4px; }
  .sd-print-btn {
    width:100%; padding:10px; border:none; border-radius:10px;
    background:#334155; color:#e2e8f0; font-size:13px; font-weight:600; cursor:pointer;
  }
  .sd-print-btn:active { background:#475569; }

  .sd-session-actions { display:flex; gap:8px; margin-top:10px; }
  .sd-session-actions .sd-print-btn { flex:1; margin-top:0; }
  .sd-close-table-btn {
    flex:1; padding:10px; border:none; border-radius:10px;
    background:linear-gradient(135deg,#f59e0b,#ea580c); color:white;
    font-size:13px; font-weight:700; cursor:pointer; font-family:inherit;
  }
  .sd-close-table-btn:active { opacity:0.8; }

  .sd-qr-btn {
    flex:1; padding:10px; border:none; border-radius:10px;
    background:#1e3a5f; color:#93c5fd;
    font-size:13px; font-weight:700; cursor:pointer; font-family:inherit;
  }
  .sd-qr-btn:active { opacity:0.8; }

  /* QR Payment Modal */
  .sd-qr-overlay {
    position:fixed; inset:0; z-index:60; background:rgba(0,0,0,0.85);
    display:flex; align-items:center; justify-content:center;
    backdrop-filter:blur(8px);
  }
  .sd-qr-modal {
    background:#ffffff; border-radius:24px; width:90%; max-width:340px;
    padding:32px 24px; text-align:center;
    animation:sd-scale-in .2s ease-out;
  }
  @keyframes sd-scale-in {
    from { transform:scale(0.9); opacity:0; }
    to { transform:scale(1); opacity:1; }
  }
  .sd-qr-header {
    font-size:14px; font-weight:700; color:#64748b; text-transform:uppercase;
    letter-spacing:1px; margin-bottom:8px;
  }
  .sd-qr-amount {
    font-size:28px; font-weight:900; color:#0f172a; margin-bottom:20px;
  }
  .sd-qr-image {
    width:100%; max-width:260px; height:auto; border-radius:12px;
    margin:0 auto 16px; display:block;
  }
  .sd-qr-bank {
    font-size:14px; font-weight:700; color:#0f172a; margin-bottom:2px;
  }
  .sd-qr-account {
    font-size:16px; font-weight:800; color:#f59e0b; letter-spacing:1px; margin-bottom:2px;
  }
  .sd-qr-holder {
    font-size:12px; font-weight:600; color:#64748b; margin-bottom:20px;
  }
  .sd-qr-close {
    width:100%; padding:14px; border:none; border-radius:14px;
    background:#0f172a; color:white; font-size:15px; font-weight:700;
    cursor:pointer; font-family:inherit;
  }

  /* Close Table Modal */
  .sd-modal-overlay {
    position:fixed; inset:0; z-index:50; background:rgba(0,0,0,0.7);
    display:flex; align-items:flex-end; justify-content:center;
    backdrop-filter:blur(4px);
  }
  .sd-modal {
    background:#1e293b; border-radius:20px 20px 0 0; width:100%; max-width:480px;
    max-height:90vh; overflow-y:auto; padding:20px 16px 24px;
    animation:sd-slide-up .25s ease-out;
  }
  @keyframes sd-slide-up {
    from { transform:translateY(100%); }
    to { transform:translateY(0); }
  }
  .sd-modal-header {
    text-align:center; margin-bottom:16px; padding-bottom:12px;
    border-bottom:1px solid #334155;
  }
  .sd-modal-title { font-weight:800; font-size:16px; }
  .sd-modal-items {
    max-height:150px; overflow-y:auto; margin-bottom:16px;
    border:1px solid #334155; border-radius:12px; padding:10px;
  }
  .sd-modal-item {
    display:flex; justify-content:space-between; font-size:13px;
    color:#cbd5e1; padding:3px 0;
  }
  .sd-modal-section { margin-bottom:14px; }
  .sd-modal-label {
    display:block; font-size:11px; font-weight:700; color:#64748b;
    text-transform:uppercase; letter-spacing:0.5px; margin-bottom:8px;
  }
  .sd-discount-row { display:flex; gap:6px; }
  .sd-discount-btn {
    flex:1; padding:10px 4px; border-radius:10px; border:1px solid #334155;
    background:#0f172a; color:#94a3b8; font-size:14px; font-weight:700;
    cursor:pointer; text-align:center; font-family:inherit;
  }
  .sd-discount-btn.active { background:#f59e0b; color:#0f172a; border-color:#f59e0b; }
  .sd-pay-row { display:flex; gap:8px; }
  .sd-pay-btn {
    flex:1; padding:12px; border-radius:12px; border:1px solid #334155;
    background:#0f172a; color:#94a3b8; font-size:14px; font-weight:700;
    cursor:pointer; text-align:center; font-family:inherit;
  }
  .sd-pay-btn.active.cash { background:#065f46; color:#6ee7b7; border-color:#065f46; }
  .sd-pay-btn.active.card { background:#1e3a5f; color:#93c5fd; border-color:#1e3a5f; }
  .sd-modal-summary {
    background:#0f172a; border-radius:12px; padding:12px; margin-bottom:16px;
  }
  .sd-summary-row {
    display:flex; justify-content:space-between; font-size:13px;
    color:#94a3b8; padding:3px 0;
  }
  .sd-summary-row.discount { color:#6ee7b7; }
  .sd-summary-row.fee { color:#93c5fd; }
  .sd-summary-total {
    display:flex; justify-content:space-between; font-size:18px;
    font-weight:800; color:#f59e0b; padding-top:8px; margin-top:6px;
    border-top:1px solid #334155;
  }
  .sd-modal-actions { display:flex; gap:10px; }
  .sd-modal-cancel {
    flex:1; padding:14px; border-radius:12px; border:1px solid #334155;
    background:transparent; color:#94a3b8; font-size:14px; font-weight:600;
    cursor:pointer; font-family:inherit;
  }
  .sd-modal-confirm {
    flex:2; padding:14px; border:none; border-radius:12px;
    background:linear-gradient(135deg,#f59e0b,#ea580c); color:white;
    font-size:14px; font-weight:700; cursor:pointer; font-family:inherit;
  }
  .sd-modal-confirm:disabled { opacity:0.5; cursor:not-allowed; }

  @media (min-width:481px) {
    .sd-root { border-left:1px solid #334155; border-right:1px solid #334155; }
  }
`;

export default StaffDashboard;
